import { DataSource } from '@angular/cdk/collections';
import cloneDeep from 'lodash-es/cloneDeep';

import isEqual from 'lodash-es/isEqual';
import { merge, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { KalTableEventManager } from './managers/kal-table-event-manager';

import { KalTableFiltersManager } from './managers/kal-table-filters-manager';
import { KalTablePageEvent, KalTablePageManager } from './managers/kal-table-page-manager';
import { KalTableSortEvent, KalTableSortManager } from './managers/kal-table-sort-manager';
import { isEmpty } from 'src/app/utils/object.utils';


export class KalTableDatasourceParameters {
    sort?: KalTableSortEvent = {code: '', direction: null};

    page?: KalTablePageEvent = {size: 50, number: 0};

    filters?: any = {};

    loading?: boolean;

    constructor(config?: KalTableDatasourceParameters) {
        if (config) {
            const {sort, page, filters, loading} = config;
            this.sort = sort || {code: '', direction: null};
            this.page = page || {size: 50, number: 0};
            this.filters = filters || {};
            this.loading = loading || false;
        }
    }
}

export type KalTableEventManagerValue = Pick<KalTableDatasourceParameters, 'filters' | 'page' | 'sort'>;

export abstract class KalTableDatasource<T> extends DataSource<T> {

    readonly name: string;

    readonly sort: KalTableSortManager;

    readonly filters: KalTableFiltersManager;

    readonly page: KalTablePageManager;

    readonly loading: KalTableEventManager<boolean>;

    constructor(config?: KalTableDatasourceParameters) {
        super();
        const {sort, filters, page, loading} = new KalTableDatasourceParameters(config);
        this.name = this.constructor.name;
        this.sort = new KalTableSortManager(sort);
        this.filters = new KalTableFiltersManager(filters);
        this.page = new KalTablePageManager(page);
        this.loading = new KalTableEventManager<boolean>(loading);
    }

    get changes$(): Observable<KalTableDatasourceParameters> {
        return merge(
            this.sort.change$,
            this.filters.change$,
            this.page.change$,
            this.loading.change$,
        ).pipe(
            filter(() => !this.loading.value),
            map(() => this.parameters),
            distinctUntilChanged((x, y) => isEqual(x, y)),
            shareReplay(),
        );
    }

    get changesWithoutLoading$(): Observable<KalTableDatasourceParameters> {
        return merge(
            this.sort.change$,
            this.filters.change$,
            this.page.change$
        ).pipe(
            map(() => this.parameters),
            distinctUntilChanged((x, y) => isEqual(x, y)),
        );
    }

    get parameters(): KalTableEventManagerValue {
        return {
            filters: this.filters.value,
            page: this.page.value,
            sort: this.sort.value
        };
    }

    set parameters(params: KalTableEventManagerValue) {
        Object.keys(params).forEach(name => {
            (this[name] as KalTableEventManager<any>).set(params[name], {emitEvent: false});
        });
    }

    get nbOfFilters(): number {
        return this.countFilters();
    }

    /**
     * Count the filters applied
     * @param exclusionFn Allow the exclusion of some filters from the count, default is isEmpty custom function
     */
    countFilters(exclusionFn: (value, key?, array?) => boolean = isEmpty): number {
        // clone filters
        const filters = cloneDeep(this.filters.value) || {};

        // remove empty keys ( like "dateCommande": {start: null, end: null} )
        Object.keys(filters).forEach(key => {
            if (exclusionFn(filters[key], key, filters)) {
                delete filters[key];
            }
        });

        return Object.keys(filters).length;
    }

    disconnect(collectionViewer?): void {
        const managersList: KalTableEventManager<any>[] = [
            this.sort,
            this.page,
            this.filters
        ];
        managersList.forEach(manager => manager.close());
    }

}
